/**
 * Price Updates Tab - Review and approve pending price changes
 *
 * Allows users to:
 * - Filter pending price updates by vendor, status, date
 * - Bulk approve/reject updates
 * - Set effective dates for approvals
 * - View detailed breakdown of individual price changes
 */

import React, { useState } from 'react'
import {
  Space,
  Card,
  Select,
  DatePicker,
  Button,
  Table,
  Tag,
  Typography,
  Tooltip,
  Alert,
  Modal,
} from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import type { TableColumnsType } from 'antd'
import { usePendingUpdates, useApprovePrices, useRejectPrices } from '../hooks/usePriceData'
import type { PriceUpdate, PriceUpdateFilters } from '../types'
import { PriceUpdateDrawer } from './PriceUpdateDrawer'

const { Text } = Typography
const { RangePicker } = DatePicker

export const PriceUpdatesTab: React.FC = () => {
  // Local state
  const [filters, setFilters] = useState<PriceUpdateFilters>({})
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [updateDrawerOpen, setUpdateDrawerOpen] = useState(false)
  const [selectedUpdate, setSelectedUpdate] = useState<PriceUpdate | null>(null)
  const [effectiveDateModalOpen, setEffectiveDateModalOpen] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState<string | undefined>()

  // Data fetching
  const { data: updates, isLoading } = usePendingUpdates(filters)

  // Mutations
  const approveMutation = useApprovePrices()
  const rejectMutation = useRejectPrices()

  // Handlers
  const handleBulkApprove = () => {
    if (selectedRows.length === 0) return

    Modal.confirm({
      title: 'Approve Selected Price Updates',
      content: `Are you sure you want to approve ${selectedRows.length} price update(s)?`,
      okText: 'Approve',
      onOk: async () => {
        await approveMutation.mutateAsync({ updateIds: selectedRows })
        setSelectedRows([])
      },
    })
  }

  const handleBulkReject = () => {
    if (selectedRows.length === 0) return

    Modal.confirm({
      title: 'Reject Selected Price Updates',
      content: `Are you sure you want to reject ${selectedRows.length} price update(s)?`,
      okText: 'Reject',
      okType: 'danger',
      onOk: async () => {
        await rejectMutation.mutateAsync({ updateIds: selectedRows })
        setSelectedRows([])
      },
    })
  }

  const handleSetEffectiveDate = () => {
    if (!effectiveDate) return

    Modal.confirm({
      title: 'Set Effective Date',
      content: `Schedule ${selectedRows.length} price update(s) for ${effectiveDate}?`,
      okText: 'Schedule',
      onOk: async () => {
        await approveMutation.mutateAsync({
          updateIds: selectedRows,
          effectiveDate,
        })
        setSelectedRows([])
        setEffectiveDateModalOpen(false)
        setEffectiveDate(undefined)
      },
    })
  }

  const handleApprove = async (updateId: string, date?: string) => {
    await approveMutation.mutateAsync({ updateIds: [updateId], effectiveDate: date })
    setUpdateDrawerOpen(false)
  }

  const handleReject = async (updateId: string, reason?: string) => {
    await rejectMutation.mutateAsync({ updateIds: [updateId], reason })
    setUpdateDrawerOpen(false)
  }

  // Table columns
  const columns: TableColumnsType<PriceUpdate> = [
    {
      title: 'SKU',
      dataIndex: 'productId',
      key: 'productId',
      width: 120,
      sorter: (a, b) => a.productId.localeCompare(b.productId),
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: { showTitle: false },
      render: (name: string) => <Tooltip title={name}>{name}</Tooltip>,
    },
    {
      title: 'Price Change',
      key: 'priceChange',
      width: 280,
      render: (_, record) => (
        <Space>
          <Text delete type="secondary">
            ${record.oldPrice.toFixed(2)}
          </Text>
          <ArrowRightOutlined style={{ color: '#8c8c8c' }} />
          <Text
            strong
            style={{
              color: record.newPrice > record.oldPrice ? '#52c41a' : '#ff4d4f',
            }}
          >
            ${record.newPrice.toFixed(2)}
          </Text>
          <Tag color={record.changePercent > 0 ? 'green' : 'red'}>
            {record.changePercent > 0 ? '+' : ''}
            {record.changePercent.toFixed(1)}%
          </Tag>
        </Space>
      ),
      sorter: (a, b) => a.changePercent - b.changePercent,
    },
    {
      title: 'Margin Impact',
      key: 'marginImpact',
      width: 180,
      render: (_, record) => (
        <Space>
          <Text type="secondary">{record.oldMargin.toFixed(1)}%</Text>
          <ArrowRightOutlined style={{ color: '#8c8c8c' }} />
          <Text strong>{record.newMargin.toFixed(1)}%</Text>
        </Space>
      ),
      sorter: (a, b) => a.newMargin - b.newMargin,
    },
    {
      title: 'Rule Applied',
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag
          color={
            status === 'pending'
              ? 'blue'
              : status === 'exception'
              ? 'red'
              : status === 'approved'
              ? 'green'
              : 'default'
          }
        >
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Exception', value: 'exception' },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ]

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Filters */}
      <Card size="small">
        <Space wrap>
          <Select
            placeholder="All Vendors"
            value={filters.vendorId}
            onChange={(v) => setFilters({ ...filters, vendorId: v })}
            style={{ width: 200 }}
            allowClear
          >
            {/* TODO: Load from vendors API */}
            <Select.Option value="vendor-1">ABC Corp</Select.Option>
            <Select.Option value="vendor-2">XYZ Inc</Select.Option>
          </Select>
          <Select
            placeholder="All Status"
            value={filters.status}
            onChange={(v) => setFilters({ ...filters, status: v })}
            style={{ width: 150 }}
            allowClear
          >
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="exception">Exceptions</Select.Option>
          </Select>
          <RangePicker
            value={filters.dateRange as any}
            onChange={(dates) =>
              setFilters({
                ...filters,
                dateRange: dates ? [dates[0]!.toISOString(), dates[1]!.toISOString()] : null,
              })
            }
          />
        </Space>
      </Card>

      {/* Bulk Actions */}
      {selectedRows.length > 0 && (
        <Card size="small">
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={selectedRows.length === 0}
              onClick={handleBulkApprove}
              loading={approveMutation.isPending}
            >
              Approve Selected ({selectedRows.length})
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              disabled={selectedRows.length === 0}
              onClick={handleBulkReject}
              loading={rejectMutation.isPending}
            >
              Reject Selected
            </Button>
            <Button
              icon={<CalendarOutlined />}
              disabled={selectedRows.length === 0}
              onClick={() => setEffectiveDateModalOpen(true)}
            >
              Set Effective Date
            </Button>
          </Space>
        </Card>
      )}

      {/* Info Alert */}
      {updates && updates.length > 0 && (
        <Alert
          message={
            <Space>
              <Text strong>
                {updates.length} price update{updates.length !== 1 ? 's' : ''} pending approval
              </Text>
              <Text type="secondary">
                ({updates.filter((u) => u.status === 'pending').length} pending,{' '}
                {updates.filter((u) => u.status === 'exception').length} exceptions)
              </Text>
            </Space>
          }
          type="info"
          showIcon
        />
      )}

      {/* Price Comparison Table */}
      <Table
        rowSelection={{
          selectedRowKeys: selectedRows,
          onChange: (keys) => setSelectedRows(keys as string[]),
        }}
        columns={columns}
        dataSource={updates?.map((u) => ({ ...u, key: u.uid })) || []}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} updates` }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedUpdate(record)
            setUpdateDrawerOpen(true)
          },
          style: { cursor: 'pointer' },
        })}
      />

      {/* Price Update Details Drawer */}
      <PriceUpdateDrawer
        open={updateDrawerOpen}
        update={selectedUpdate}
        onClose={() => setUpdateDrawerOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      {/* Effective Date Modal */}
      <Modal
        title="Set Effective Date"
        open={effectiveDateModalOpen}
        onOk={handleSetEffectiveDate}
        onCancel={() => {
          setEffectiveDateModalOpen(false)
          setEffectiveDate(undefined)
        }}
        okText="Schedule"
        okButtonProps={{ disabled: !effectiveDate }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Schedule {selectedRows.length} price update{selectedRows.length !== 1 ? 's' : ''} to take
            effect on a specific date:
          </Text>
          <DatePicker
            style={{ width: '100%' }}
            onChange={(date) => setEffectiveDate(date?.toISOString())}
          />
        </Space>
      </Modal>
    </Space>
  )
}
